import { useState, useEffect, useRef } from 'react';
import { getCategories, createCategory, updateCategory, deleteCategory, uploadCategoryImage } from '../services/api';

const BASE = 'http://localhost:8000';

export default function Categories() {
  const [cats, setCats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState(null);
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [search, setSearch] = useState('');
  const editRef = useRef();

  const load = async () => {
    setLoading(true);
    try { setCats(await getCategories()); } catch (_) {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    if (!name.trim()) return alert('Name is required');
    try {
      if (editId) {
        await updateCategory(editId, { name: name.trim(), description: desc.trim() });
      } else {
        await createCategory({ name: name.trim(), description: desc.trim() });
      }
      setShowForm(false); setEditId(null); setName(''); setDesc('');
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error saving category');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this category?')) return;
    try { await deleteCategory(id); load(); } catch (e) {
      alert(e.response?.data?.detail || 'Error deleting');
    }
  };

  const handleUpload = async (id, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      await uploadCategoryImage(id, file);
      e.target.value = '';
      load();
    } catch (err) {
      alert(err.response?.data?.detail || 'Upload failed');
    }
  };

  const startEdit = (cat) => {
    setEditId(cat.id);
    setName(cat.name);
    setDesc(cat.description || '');
    setShowForm(true);
    setTimeout(() => editRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }), 100);
  };

  const filtered = cats.filter(c => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return String(c.id).includes(q) || c.name.toLowerCase().includes(q);
  });

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2>📂 Categories ({cats.length})</h2>
        <button onClick={() => { setShowForm(!showForm); setEditId(null); setName(''); setDesc(''); }}
          style={btnStyle}>{showForm && !editId ? '✕ Cancel' : '+ Add Category'}</button>
      </div>

      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input placeholder="🔍 Search by ID or name..." value={search} onChange={e => setSearch(e.target.value)}
          style={{ ...inputStyle, maxWidth: 400 }} />
      </div>

      {/* Add form (only for new, not edit) */}
      {showForm && !editId && (
        <div style={formBox}>
          <h3>New Category</h3>
          <input placeholder="Category Name" value={name} onChange={e => setName(e.target.value)} style={inputStyle} />
          <input placeholder="Description (optional)" value={desc} onChange={e => setDesc(e.target.value)} style={inputStyle} />
          <button onClick={handleSave} style={{ ...btnStyle, width: '100%' }}>➕ Create</button>
        </div>
      )}

      {loading ? <p>Loading...</p> : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: 16 }}>
          {filtered.map(cat => (
            <div key={cat.id} style={cardStyle}>
              {/* Image */}
              <div style={{ width: '100%', height: 140, borderRadius: '12px 12px 0 0', overflow: 'hidden', background: '#f0f0f0', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                {cat.image_url ? (
                  <img src={`${BASE}${cat.image_url}`} alt={cat.name}
                    style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                    onError={e => { e.target.style.display = 'none'; }} />
                ) : (
                  <span style={{ fontSize: 40, opacity: 0.3 }}>📂</span>
                )}
              </div>
              <div style={{ padding: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <h3 style={{ margin: '0 0 4px' }}>{cat.name}</h3>
                  <span style={{ fontSize: 11, color: '#aaa' }}>#{cat.id}</span>
                </div>
                <p style={{ margin: '0 0 12px', color: '#888', fontSize: 13 }}>{cat.description || 'No description'}</p>

                {/* Inline edit form */}
                {showForm && editId === cat.id && (
                  <div ref={editRef} style={{ background: '#f8f5ff', padding: 14, borderRadius: 10, marginBottom: 12, border: '1px solid #d4c4ff' }}>
                    <input placeholder="Category Name" value={name} onChange={e => setName(e.target.value)}
                      style={{ ...inputStyle, marginBottom: 8 }} />
                    <input placeholder="Description" value={desc} onChange={e => setDesc(e.target.value)}
                      style={{ ...inputStyle, marginBottom: 8 }} />
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button onClick={handleSave} style={{ ...btnStyle, flex: 1, fontSize: 12, padding: '8px 12px' }}>💾 Update</button>
                      <button onClick={() => { setShowForm(false); setEditId(null); }}
                        style={{ ...smallBtn, flex: 1 }}>Cancel</button>
                    </div>
                  </div>
                )}

                {/* Upload image */}
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 10 }}>
                  <input type="file" accept=".jpg,.jpeg,.jfif,.png,.gif,.webp,.avif,.bmp,.svg"
                    style={{ fontSize: 12, flex: 1 }}
                    onChange={(e) => handleUpload(cat.id, e)} />
                </div>

                {/* Actions */}
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => startEdit(cat)} style={smallBtn}>✏️ Edit</button>
                  <button onClick={() => handleDelete(cat.id)} style={{ ...smallBtn, background: '#fee', color: '#d32f2f' }}>🗑️ Delete</button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const btnStyle = { padding: '10px 20px', background: '#6C63FF', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontWeight: 'bold', fontSize: 14 };
const smallBtn = { padding: '6px 14px', background: '#f5f5f5', border: '1px solid #ddd', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600 };
const inputStyle = { width: '100%', padding: '10px 14px', border: '1px solid #ddd', borderRadius: 10, fontSize: 14, marginBottom: 10, boxSizing: 'border-box' };
const formBox = { background: '#fff', padding: 20, borderRadius: 16, boxShadow: '0 2px 12px rgba(0,0,0,0.06)', marginBottom: 20, maxWidth: 400 };
const cardStyle = { background: '#fff', borderRadius: 12, boxShadow: '0 2px 10px rgba(0,0,0,0.06)', overflow: 'hidden' };
